// composables/utils/useFlagging.ts

//
// ============================================================================
// IMPORTS
// ============================================================================
//

import type { FlagItem, FlagSource } from "../../services/api/domains/pending/pending.types";

//
// ============================================================================
// TYPES
// ============================================================================
//

interface FlaggingCalculationResult {
  textResult: string;
  decimalResult: number;
  flagLevel: number;
}

interface FlaggingResult {
  calculationResult: FlaggingCalculationResult;
  refId: string;
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Result Flagging Composable
 *
 * Provides utilities for laboratory result flagging visualization and interpretation.
 * Handles conversion between backend flag levels (1-7) and UI display (icons, colors, text).
 *
 * Responsibilities:
 * - Map backend flag levels to UI colors and gradients
 * - Provide flag icons based on level (up/down arrows, check marks)
 * - Generate human-readable flag descriptions
 * - Convert backend levels (1-7) to UI levels (0-4) for dialog display
 * - Create FlagItem objects from backend flag levels
 * - Support both gradient backgrounds (result cells) and dialog display
 */
export const useFlagging = () => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //
  const { t } = useI18n();

  //
  // ============================================================================
  // CONSTANTS
  // ============================================================================
  //

  /**
   * Flag level to color mapping (for gradients)
   */
  const FLAG_COLORS: Record<number, string> = {
    0: "#FFFFFF00", // None (transparent)
    1: "#7986CB", // Low
    2: "#9FA8DA", // Medium-Low
    3: "#C5CAE9", // Medium
    4: "#FFFFFF00", // Normal (transparent)
    5: "#FFCDD2", // Medium-High
    6: "#EF9A9A", // High
    7: "#E57373", // Critical
  };

  //
  // ============================================================================
  // METHODS - GRADIENT VISUALIZATION
  // ============================================================================
  //

  /**
   * Get flag color by level (for gradients)
   *
   * Purpose:
   * Retrieve hex color code for a specific flag level.
   * Used for gradient backgrounds in result cells.
   *
   * Responsibilities:
   * - Map flag level to predefined color
   * - Fallback to transparent if level not found
   *
   * @param flagLevel - Backend flag level (0-7)
   * @returns Hex color code with alpha channel
   */
  const getFlagColor = (flagLevel: number): string => {
    return FLAG_COLORS[flagLevel] ?? FLAG_COLORS[0] ?? "#FFFFFF00";
  };

  /**
   * Get gradient background for flag level
   *
   * Purpose:
   * Generate CSS linear gradient for result cell backgrounds.
   * Gradient fades from transparent (left) to flag color (right).
   *
   * Responsibilities:
   * - Get color for flag level
   * - Create left-to-right gradient
   * - Format as CSS linear-gradient string
   *
   * @param flagLevel - Backend flag level (0-7)
   * @returns CSS linear-gradient string
   */
  const getFlagGradient = (flagLevel: number): string => {
    const color = getFlagColor(flagLevel);
    return `linear-gradient(to left, #FFFFFF00, ${color})`;
  };

  /**
   * Find flag level for a result by its ID
   *
   * Purpose:
   * Lookup flag level for a specific result from flagging calculation response.
   * Used to apply correct styling to result cells.
   *
   * Responsibilities:
   * - Search flagging results array by result ID
   * - Extract flag level from calculation result
   * - Return 0 (no flag) if result not found or no data
   *
   * @param resultId - Result identifier (number or string)
   * @param flaggingResults - Array of flagging calculation results
   * @returns Flag level (0-7), 0 if not found
   */
  const getFlagLevel = (resultId: number | string, flaggingResults: FlaggingResult[]): number => {
    if (!flaggingResults || flaggingResults.length === 0) return 0;

    const flagItem = flaggingResults.find((item) => item.refId === resultId.toString());

    return flagItem?.calculationResult?.flagLevel ?? 0;
  };

  /**
   * Get flag icon by backend level (1-7)
   *
   * Purpose:
   * Map flag level to Material Design Icon for visual indication.
   * Icons show direction of abnormality (up/down arrows) or normal (check).
   *
   * Responsibilities:
   * - Return down arrow for low values (1-3)
   * - Return up arrow for high values (5-7)
   * - Return check mark for normal (4) or unknown (0)
   *
   * @param flagLevel - Backend flag level (0-7)
   * @returns Material Design Icon name
   */
  const getFlagIcon = (flagLevel: number): string => {
    switch (flagLevel) {
      case 1:
      case 2:
      case 3:
        return "mdi-arrow-down-circle-outline";
      case 5:
      case 6:
      case 7:
        return "mdi-arrow-up-circle-outline";
      default:
        return "mdi-check-circle-outline";
    }
  };

  /**
   * Get clinical shorthand for flag level (backend level 0-7)
   *
   * Purpose:
   * Return the industry-standard clinical notation for a LIS reference-range
   * flag level as it appears in a patient history table.
   * Used in the PatientHistoryDialog flags column.
   *
   * Mapping confirmed against the i18n flag descriptions in
   * `i18n/modules/en/pages/validation.ts` (flags.descriptions):
   *   1 = LLL  (significantly below reference range — most extreme low)
   *   2 = LL   (below reference range)
   *   3 = L    (slightly below normal — mildest low)
   *   4 = ""   (within normal limits — nothing shown)
   *   5 = H    (slightly above normal — mildest high)
   *   6 = HH   (above reference range)
   *   7 = HHH  (critically high — most extreme high)
   *
   * @param flagLevel - Backend flag level (0-7)
   * @returns Clinical shorthand string, or empty string for normal/unknown
   */
  const getFlagShortText = (flagLevel: number): string => {
    switch (flagLevel) {
      case 1:
        return "LLL"; // Significantly below reference range
      case 2:
        return "LL"; // Below reference range
      case 3:
        return "L"; // Slightly below normal
      case 5:
        return "H"; // Slightly above normal
      case 6:
        return "HH"; // Above reference range
      case 7:
        return "HHH"; // Critically high
      default:
        return ""; // 0 (unknown) and 4 (normal) show nothing
    }
  };

  /**
   * Get flag text description (backend level 1-7)
   *
   * Purpose:
   * Provide human-readable English text for flag level.
   * Used for tooltips, table headers, and accessibility.
   *
   * Responsibilities:
   * - Map backend level to English description
   * - Return "Unknown" for invalid levels
   *
   * @param flagLevel - Backend flag level (0-7)
   * @returns English description string
   */
  const getFlagText = (flagLevel: number): string => {
    switch (flagLevel) {
      case 1:
        return "Low";
      case 2:
        return "Medium-Low";
      case 3:
        return "Below Normal";
      case 4:
        return "Normal";
      case 5:
        return "Above Normal";
      case 6:
        return "High";
      case 7:
        return "Critical";
      default:
        return "Unknown";
    }
  };

  //
  // ============================================================================
  // METHODS - FLAG DIALOG HELPERS
  // ============================================================================
  //

  /**
   * Get flag icon for dialog (mapped level 0-4)
   *
   * Purpose:
   * Provide appropriate icon for flag detail dialog.
   * Uses UI-level mapping (0-4) instead of backend levels (1-7).
   *
   * Responsibilities:
   * - Map UI level to contextual icon
   * - Support information, warning, error, critical severity levels
   *
   * @param mappedLevel - UI flag level (0-4)
   * @returns Material Design Icon name
   */
  const getFlagDialogIcon = (mappedLevel: number): string => {
    switch (mappedLevel) {
      case 1: // Information
        return "mdi-information-outline";
      case 2: // Warning
        return "mdi-alert-outline";
      case 3: // Error
        return "mdi-alert";
      case 4: // Critical
        return "mdi-alert-octagon";
      default: // Unknown
        return "mdi-help-circle-outline";
    }
  };

  /**
   * Get flag color for dialog (mapped level 0-4)
   *
   * Purpose:
   * Provide Vuetify color name for flag dialog styling.
   * Colors match severity level of the flag.
   *
   * Responsibilities:
   * - Map UI level to Vuetify color name
   * - Support consistent color scheme across dialogs
   *
   * @param mappedLevel - UI flag level (0-4)
   * @returns Vuetify color name
   */
  const getFlagDialogColor = (mappedLevel: number): string => {
    switch (mappedLevel) {
      case 1: // Information
        return "grey";
      case 2: // Warning
        return "orange";
      case 3: // Error
        return "red";
      case 4: // Critical
        return "red";
      default: // Unknown
        return "grey-darken-2";
    }
  };

  /**
   * Get translated flag level text (mapped level 0-4)
   *
   * Purpose:
   * Retrieve localized text for UI flag level.
   * Supports multi-language interface (bg/mk/en).
   *
   * Responsibilities:
   * - Look up translation key for mapped level
   * - Return localized text via i18n
   *
   * @param mappedLevel - UI flag level (0-4)
   * @returns Localized level description
   */
  const getFlagDialogLevelText = (mappedLevel: number): string => {
    const key = `pages.validation.dialogs.flags.levels.${mappedLevel}`;
    return t(key);
  };

  //
  // ============================================================================
  // METHODS - FLAG GENERATION
  // ============================================================================
  //

  /**
   * Map backend flag level (1-7) to UI system (0-4)
   *
   * Purpose:
   * Convert detailed backend flagging levels to simplified UI severity levels.
   * Reduces complexity for dialog display while preserving clinical meaning.
   *
   * Responsibilities:
   * - Group backend levels into UI severity categories
   * - Map normal (4) to no flag (0)
   * - Handle unknown/invalid levels
   *
   * @param backendLevel - Backend flag level (0-7)
   * @returns UI flag level (0-4)
   */
  const mapFlagLevel = (backendLevel: number): number => {
    switch (backendLevel) {
      case 0:
        return 0; // Unknown
      case 1:
      case 2:
      case 3:
        return 1; // Information/Low
      case 4:
        return 0; // Normal - no flag
      case 5:
        return 2; // Warning/Above normal
      case 6:
        return 3; // Error/High
      case 7:
        return 4; // Critical/Panic
      default:
        return 0;
    }
  };

  /**
   * Get flag description based on backend flag level (1-7)
   *
   * Purpose:
   * Retrieve localized description explaining what the flag level means.
   * Used in flag detail dialogs to help clinicians interpret results.
   *
   * Responsibilities:
   * - Map backend level to i18n translation key
   * - Return localized clinical interpretation
   * - Handle all flag levels including unknown
   *
   * @param backendLevel - Backend flag level (0-7)
   * @returns Localized flag description
   */
  const getFlagDescription = (backendLevel: number): string => {
    switch (backendLevel) {
      case 1:
        return t("pages.validation.flags.descriptions.1");
      case 2:
        return t("pages.validation.flags.descriptions.2");
      case 3:
        return t("pages.validation.flags.descriptions.3");
      case 4:
        return t("pages.validation.flags.descriptions.4");
      case 5:
        return t("pages.validation.flags.descriptions.5");
      case 6:
        return t("pages.validation.flags.descriptions.6");
      case 7:
        return t("pages.validation.flags.descriptions.7");
      default:
        return t("pages.validation.flags.descriptions.0");
    }
  };

  /**
   * Generate flag items from backend flag level (1-7)
   *
   * Purpose:
   * Create FlagItem array from simple backend flag level.
   * Backend doesn't send detailed flag objects, so we generate them client-side.
   *
   * Responsibilities:
   * - Skip flag generation for Unknown (0) or Normal (4)
   * - Map backend level to UI level
   * - Create FlagItem with metadata (level, source, code, description)
   * - Return empty array if no flagging needed
   *
   * @param backendLevel - Backend flag level (0-7)
   * @returns Array of FlagItem (empty for normal/unknown)
   */
  const generateFlagsFromLevel = (backendLevel: number): FlagItem[] => {
    if (!backendLevel || backendLevel === 0 || backendLevel === 4) {
      return []; // No flags for Unknown (0) or Normal (4)
    }

    const mappedLevel = mapFlagLevel(backendLevel);

    return [
      {
        level: mappedLevel,
        source: "Processor" as FlagSource,
        code: `FLAG-${backendLevel}`,
        description: getFlagDescription(backendLevel),
      },
    ];
  };

  return {
    // Gradient colors (for result cells)
    getFlagColor,
    getFlagGradient,
    getFlagLevel,
    getFlagIcon,
    getFlagText,
    getFlagShortText,

    // Dialog helpers
    getFlagDialogIcon,
    getFlagDialogColor,
    getFlagDialogLevelText,

    // Flag generation
    generateFlagsFromLevel,
    mapFlagLevel,
    getFlagDescription,
  };
};
